"use client";

// Durable mirror of the in-memory pending-attachments holder. We need this
// because the in-memory holder ([[pending-attachments]]) dies on a full-page
// redirect — which is exactly what happens on OAuth round-trips
// (Google/Facebook/LinkedIn). localStorage can't hold File objects, but
// IndexedDB can (structured clone supports File natively), so a brief
// detour through IDB lets the prompt-resume flow restore the user's picks
// after they come back from the provider.
//
// Mirrors the 60-minute TTL of [[home-prompt-stash]] so prompt + attachments
// expire together. Single fixed key — there is at most one in-flight
// "build pending auth" per browser session, so no UUID coordination needed.

const DB_NAME = "panda";
const STORE_NAME = "pending_attachments";
const DB_VERSION = 1;
const ENTRY_KEY = "current";
const TTL_MS = 60 * 60 * 1000;

type Entry = {
  files: File[];
  timestamp: number;
};

function openDb(): Promise<IDBDatabase | null> {
  if (typeof indexedDB === "undefined") return Promise.resolve(null);
  return new Promise((resolve) => {
    let req: IDBOpenDBRequest;
    try {
      req = indexedDB.open(DB_NAME, DB_VERSION);
    } catch {
      resolve(null);
      return;
    }
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => resolve(null);
    req.onblocked = () => resolve(null);
  });
}

function txAwait<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

// Await transaction commit, not just request success. Critical for the
// OAuth handoff: the next thing after `await persistPendingAttachments` is
// `window.location.assign(provider_url)`, which tears the page down. If we
// returned on req.onsuccess (which can fire before the tx commits), the
// browser kills the half-committed transaction and IDB ends up empty.
function awaitTx(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error ?? new Error("idb tx aborted"));
  });
}

export async function persistPendingAttachments(files: File[]): Promise<void> {
  if (typeof window === "undefined") return;
  if (files.length === 0) {
    await clearPersistedAttachments();
    return;
  }
  const db = await openDb();
  if (!db) {
    console.warn("[idb:persist] openDb returned null — IDB unavailable");
    return;
  }
  try {
    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);
    const entry: Entry = { files, timestamp: Date.now() };
    store.put(entry, ENTRY_KEY);
    await awaitTx(tx);
    console.log("[idb:persist] wrote", files.length, "files", {
      names: files.map((f) => f.name),
      totalBytes: files.reduce((s, f) => s + f.size, 0),
    });
  } catch (err) {
    console.warn("[idb:persist] failed:", err);
  } finally {
    db.close();
  }
}

/**
 * Read WITHOUT deleting — for callers that need to know what is pending
 * before the flow that actually consumes it runs (the first-turn intent gate
 * must see the attachment before it routes, but the upload further down still
 * needs the files). Same TTL rules as restore; returns [] on miss, expired,
 * or error. Deliberately read-only: peeking must never be able to eat the
 * entry a later restore depends on.
 */
export async function peekPersistedAttachments(): Promise<File[]> {
  if (typeof window === "undefined") return [];
  const db = await openDb();
  if (!db) return [];
  try {
    const tx = db.transaction(STORE_NAME, "readonly");
    const entry = (await txAwait(
      tx.objectStore(STORE_NAME).get(ENTRY_KEY),
    )) as Entry | undefined;
    if (!entry) return [];
    if (
      !Array.isArray(entry.files) ||
      typeof entry.timestamp !== "number" ||
      Date.now() - entry.timestamp > TTL_MS
    ) {
      return [];
    }
    return entry.files.filter((f): f is File => f instanceof File);
  } catch (err) {
    console.warn("[idb:peek] failed:", err);
    return [];
  } finally {
    db.close();
  }
}

/**
 * Read-and-delete in the same transaction so a back nav after restore
 * doesn't re-attach stale files. Returns [] on miss, expired, or error.
 */
export async function restorePendingAttachments(): Promise<File[]> {
  if (typeof window === "undefined") return [];
  const db = await openDb();
  if (!db) {
    console.warn("[idb:restore] openDb returned null — IDB unavailable");
    return [];
  }
  try {
    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);
    const entry = (await txAwait(store.get(ENTRY_KEY))) as Entry | undefined;
    store.delete(ENTRY_KEY);
    await awaitTx(tx);
    if (!entry) {
      console.log("[idb:restore] no entry");
      return [];
    }
    if (
      !Array.isArray(entry.files) ||
      typeof entry.timestamp !== "number" ||
      Date.now() - entry.timestamp > TTL_MS
    ) {
      console.log("[idb:restore] entry expired or malformed", {
        ageMs: Date.now() - entry.timestamp,
        ttlMs: TTL_MS,
      });
      return [];
    }
    const files = entry.files.filter((f): f is File => f instanceof File);
    console.log("[idb:restore] restored", files.length, "files", {
      names: files.map((f) => f.name),
    });
    return files;
  } catch (err) {
    console.warn("[idb:restore] failed:", err);
    return [];
  } finally {
    db.close();
  }
}

export async function clearPersistedAttachments(): Promise<void> {
  if (typeof window === "undefined") return;
  const db = await openDb();
  if (!db) return;
  try {
    const tx = db.transaction(STORE_NAME, "readwrite");
    await txAwait(tx.objectStore(STORE_NAME).delete(ENTRY_KEY));
  } catch {
    // ignore
  } finally {
    db.close();
  }
}
